import { ACCESSIBILITY_PATH } from '@hunk/shared/api/pages';
import { Accessibility, Contrast, Droplet, Link2, MousePointer2, PauseCircle, RotateCcw, Type, X, ZoomIn, ZoomOut } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { ROUTER_BASENAME } from '@/appMode';
import { cn } from '@/lib/utils';

/**
 * The accessibility menu ("נגישות") found on Israeli sites: text size, high contrast, grayscale, highlighted links,
 * a readable font, no animations, a big cursor + a link to the accessibility statement. Each option is a class on
 * <html> (styles in index.css), remembered in this browser. applyStoredA11y() runs before the first render.
 */

interface A11ySettings {
  /** text size step: 0 = normal, 1..5 = +10% each */
  zoom: number;
  contrast: boolean;
  grayscale: boolean;
  links: boolean;
  font: boolean;
  still: boolean;
  cursor: boolean;
}
type Flag = Exclude<keyof A11ySettings, 'zoom'>;

const DEFAULTS: A11ySettings = { zoom: 0, contrast: false, grayscale: false, links: false, font: false, still: false, cursor: false };
const MAX_ZOOM = 5;
const KEY = 'hunk.a11y';
const FLAGS: { key: Flag; label: string; icon: ReactNode }[] = [
  { key: 'contrast', label: 'ניגודיות גבוהה', icon: <Contrast /> },
  { key: 'grayscale', label: 'גווני אפור', icon: <Droplet /> },
  { key: 'links', label: 'הדגשת קישורים', icon: <Link2 /> },
  { key: 'font', label: 'גופן קריא', icon: <Type /> },
  { key: 'still', label: 'עצירת אנימציות', icon: <PauseCircle /> },
  { key: 'cursor', label: 'סמן גדול', icon: <MousePointer2 /> }
];

function load(): A11ySettings {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<A11ySettings>) };
  } catch {
    return DEFAULTS;
  }
}

function apply(s: A11ySettings) {
  const html = document.documentElement;
  for (const { key } of FLAGS) html.classList.toggle(`a11y-${key}`, s[key]);
  html.style.fontSize = s.zoom ? `${100 + s.zoom * 10}%` : '';
}

/** before the first render, so a saved setting never flashes off */
export const applyStoredA11y = () => apply(load());

/** inline: a header button with the panel dropping down under it; floating: a fixed corner button (the age gate) */
export function AccessibilityWidget({ floating = false }: { floating?: boolean }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(load);
  const panel = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    apply(s);
    try {
      localStorage.setItem(KEY, JSON.stringify(s));
    } catch {
      // private mode - the settings last until the tab closes
    }
  }, [s]);

  // Esc / a click outside closes; focus goes into the panel and back to the button
  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !button.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  const zoom = (d: number) => setS((v) => ({ ...v, zoom: Math.min(MAX_ZOOM, Math.max(0, v.zoom + d)) }));
  const changed = JSON.stringify(s) !== JSON.stringify(DEFAULTS);

  return (
    <div dir="rtl" className={cn('a11y-widget text-base', floating ? 'fixed bottom-[calc(1rem+var(--tg-bottom))] left-4 z-[110]' : 'relative')}>
      {open && (
        <div
          ref={panel}
          id="a11y-panel"
          role="dialog"
          aria-label="תפריט נגישות"
          className={cn(
            'absolute left-0 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-2xl border border-white/15 bg-neutral-950 p-4 text-white shadow-2xl',
            floating ? 'bottom-16' : 'top-full mt-2'
          )}
        >
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-bold">
              <Accessibility className="size-5" />
              נגישות
            </h2>
            <button type="button" onClick={() => setOpen(false)} aria-label="סגירת תפריט הנגישות" className="rounded-full p-1 hover:bg-white/10">
              <X className="size-5" />
            </button>
          </div>

          <div className="mb-2 flex items-center justify-between rounded-xl border border-white/10 p-2">
            <span className="text-sm font-semibold">גודל טקסט</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => zoom(-1)} disabled={s.zoom === 0} aria-label="הקטנת טקסט" className="rounded-lg p-1.5 hover:bg-white/10 disabled:opacity-40">
                <ZoomOut className="size-5" />
              </button>
              <span className="w-12 text-center text-sm tabular-nums" aria-live="polite">
                {100 + s.zoom * 10}%
              </span>
              <button
                type="button"
                onClick={() => zoom(1)}
                disabled={s.zoom === MAX_ZOOM}
                aria-label="הגדלת טקסט"
                className="rounded-lg p-1.5 hover:bg-white/10 disabled:opacity-40"
              >
                <ZoomIn className="size-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {FLAGS.map(({ key, label, icon }) => (
              <button
                key={key}
                type="button"
                aria-pressed={s[key]}
                onClick={() => setS((v) => ({ ...v, [key]: !v[key] }))}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-xl border p-2.5 text-sm font-semibold [&_svg]:size-5',
                  s[key] ? 'border-primary bg-primary/20' : 'border-white/10 hover:bg-white/5'
                )}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>

          <div className="mt-3 flex items-center justify-between text-sm">
            <button type="button" onClick={() => setS(DEFAULTS)} disabled={!changed} className="flex items-center gap-1 text-neutral-300 hover:text-white disabled:opacity-40">
              <RotateCcw className="size-4" />
              איפוס הגדרות
            </button>
            <a href={`${ROUTER_BASENAME.replace(/\/$/, '')}${ACCESSIBILITY_PATH}`} className="text-primary font-semibold underline">
              הצהרת נגישות
            </a>
          </div>
        </div>
      )}
      <button
        ref={button}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-label="תפריט נגישות"
        title="נגישות"
        className={cn(
          'flex items-center justify-center rounded-full bg-[#1a56db] text-white hover:bg-[#1e4fc2] focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-yellow-300',
          floating ? 'size-12 border-2 border-white shadow-lg' : 'size-9 md:size-10'
        )}
      >
        <Accessibility className={floating ? 'size-7' : 'size-5 md:size-6'} />
      </button>
    </div>
  );
}
